// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import { find, forIn, groupBy, isEmpty } from 'lodash'
import React from 'react'
import { markFirstActivityOfEmployees } from '../../common/utils'
import type { User } from '../admin/users/userTypes'
import withUser from '../auth/withUser'
import { i18n } from '../index'
import type { ShiftResponsibility } from '../shiftResponsibility/shiftResponsibilityTypes'
import EmployeeRow from './EmployeeRow'
import EmployeeTableHeader from './EmployeeTableHeader'
import type { StaffMember } from './types'
// import type {ActivityDescription} from '../activityDescription/ActivityDescriptionTypes'
import { Stack, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import cn from 'classnames'
import moment from 'moment'
import { Station } from '../station/stationTypes'
import EmployeeCard from './EmployeeCard'
import css from './EmployeeTable.css'
import ExpandableEmployeeRow from './ExpandableEmployeeRow'

type ActivityDescription = {
  id: string
  name: string
  description: string
  station: Station
}

type Props = {
  employees: Array<StaffMember>
  updateEmployees: (...args: Array<any>) => any
  toggleSortColumn: (...args: Array<any>) => any
  toggleSortDirection: (...args: Array<any>) => any
  sortInfo: Array<{
    col: string
    direction: string
  }>
  toggleEditNote: (arg0: string) => void
  noteIDs: Array<string>
  isEditable: boolean
  data: Record<string, any>
  removeStaffMember: (...args: Array<any>) => any
  stationId: string
  date: string
  isToday: boolean
  user: User
  shiftResponsibilities: Array<ShiftResponsibility>
  activityDescriptions: Array<ActivityDescription>
  submit: (...args: Array<any>) => any
  totalIncentivesTarget: number | null
  totalThumbablesTarget: number | null
  chainIncentiveSalesTarget: number
  incentiveSalesTargetSum: number | null
  isMobile: boolean
}

type State = {
  isEmployeeRowDialogOpen: boolean
  employeeRowDialogContent: string
  expandedEmployees: string[]
  allExpanded: boolean
}

@withUser
class EmployeeTableBase extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isEmployeeRowDialogOpen: false,
      employeeRowDialogContent: '',
      expandedEmployees: [],
      allExpanded: false,
    }
  }

  componentDidMount() {
    const { data } = this.props

    if (!isEmpty(data)) {
      this.updateEmployeesFromData(data)
    }
  }

  handleInput = (value: string | Array<string>, field: string) => {
    if (field === 'salesIncentiveTarget' || field === 'thumbsUpTarget') {
      return parseInt(value, 10) || 0
    } else {
      return value
    }
  }
  onChange = (idx: number, field: string, value: string | Array<string>) => {
    return new Promise((res) => {
      const { updateEmployees, employees } = this.props
      const newValue = this.handleInput(value, field)
      const selectedEmployees = [...employees]
      const employee = selectedEmployees[idx]
      employee[field] = newValue
      updateEmployees(selectedEmployees)
      res()
    })
  }
  updateEmployeesFromData = (data: any) => {
    const { updateEmployees } = this.props
    const employees = markFirstActivityOfEmployees(
      [...data].map((employee) => ({
        ...employee,
        userId: employee.user.id,
        user: {
          name: `${employee.user.name}`,
        },
      })),
    )
    updateEmployees(employees)
  }
  openEmployeeRowDialog = (content: string) => {
    this.setState({
      isEmployeeRowDialogOpen: true,
      employeeRowDialogContent: content,
    })
  }
  closeEmployeeRowDialog = () => {
    this.setState({
      isEmployeeRowDialogOpen: false,
    })
  }
  toggleExpandedEmployee = (userId: string) => {
    const { expandedEmployees, allExpanded } = this.state
    let updated = [...expandedEmployees]

    if (expandedEmployees.includes(userId)) {
      updated = updated.filter((x) => x !== userId)
    } else {
      updated.push(userId)
    }

    const updateAllExpanded = updated.length > 0 ? allExpanded : false
    this.setState({
      expandedEmployees: updated,
      allExpanded: updateAllExpanded,
    })
  }
  toggleExpandAll = () => {
    const { employees } = this.props
    const { allExpanded } = this.state
    const groupedEmployees = groupBy(employees, (emp) => emp.userId)
    const allExpandableEmployeeUserIds = []
    forIn(groupedEmployees, (val, key) => {
      if (val.length > 1) {
        allExpandableEmployeeUserIds.push(key)
      }
    })

    if (allExpanded) {
      this.setState({
        expandedEmployees: [],
        allExpanded: false,
      })
    } else {
      this.setState({
        expandedEmployees: allExpandableEmployeeUserIds,
        allExpanded: true,
      })
    }
  }
  getCurrentIncentiveTargetSums = (): {
    currentIncentivesTargetSum: number
    currentThumbablesTargetSum: number
  } => {
    const { employees } = this.props
    return employees.reduce(
      (cur, prev) => {
        return {
          currentIncentivesTargetSum: cur.currentIncentivesTargetSum + prev.salesIncentiveTarget,
          currentThumbablesTargetSum: cur.currentThumbablesTargetSum + prev.thumbsUpTarget,
        }
      },
      {
        currentIncentivesTargetSum: 0,
        currentThumbablesTargetSum: 0,
      },
    )
  }

  renderEmployeeCards = ({
    groupedEmployees,
    isEditable,
    isToday,
    userType,
    shiftResponsibilities,
    activityDescriptions,
    defaultDescription,
    removeStaffMember,
    submit,
    toggleEditNote,
    noteIDs,
    employees,
  }) => {
    return (
      <Stack component='ul' spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {Object.keys(groupedEmployees).map((userId) => {
          const employeeRows = groupedEmployees[userId]
          const employeeIncentives = employeeRows.reduce(
            (prev, cur) => ({
              sia: prev.sia + cur.salesIncentiveActual,
              sit: prev.sit + cur.salesIncentiveTarget,
              tua: prev.tua + cur.thumbsUpActual,
              tut: prev.tut + cur.thumbsUpTarget,
            }),
            { sia: 0, sit: 0, tua: 0, tut: 0 },
          )
          return getSortedEmployeeRows(employeeRows).map((employee, idx) => {
            const uniqID = employee.userId + idx
            const activityDescription = find(activityDescriptions, (a) => a.name === employee.position)
            const rowIndex = employees.indexOf(employee)
            return (
              <EmployeeCard
                key={uniqID}
                idx={rowIndex}
                employee={employee}
                userType={userType}
                removeStaffMember={removeStaffMember}
                valueChanged={this.onChange}
                isEditing={isEditable}
                toggleEditNote={() => toggleEditNote(uniqID)}
                editNote={noteIDs.findIndex((note) => note === uniqID) > -1}
                isToday={isToday}
                shiftResponsibilities={shiftResponsibilities}
                activityDescription={activityDescription ? activityDescription.description : defaultDescription}
                openEmployeeRowDialog={this.openEmployeeRowDialog}
                submit={submit}
                employeeIncentives={employeeIncentives}
              />
            )
          })
        })}
      </Stack>
    )
  }

  render() {
    const {
      toggleSortColumn,
      toggleSortDirection,
      sortInfo,
      toggleEditNote,
      noteIDs,
      isEditable,
      employees,
      removeStaffMember,
      isToday,
      shiftResponsibilities,
      activityDescriptions,
      user: { userType },
      submit,
      totalIncentivesTarget,
      totalThumbablesTarget,
      user,
      chainIncentiveSalesTarget,
      incentiveSalesTargetSum,
      isMobile,
    } = this.props
    const { isEmployeeRowDialogOpen, employeeRowDialogContent, expandedEmployees, allExpanded } = this.state
    const defaultDescription = `${i18n.t('common.morningShift')}:

${i18n.t('common.eveningShift')}:

${i18n.t('common.nightShift')}:`
    const groupedEmployees = groupBy(employees, (emp) => emp.userId)
    const hasExpandableEmployees = Object.values(groupedEmployees).some((x) => x.length > 1)
    const { currentIncentivesTargetSum, currentThumbablesTargetSum } = this.getCurrentIncentiveTargetSums()
    return (
      <React.Fragment>
        {hasExpandableEmployees && (
          <Button onClick={this.toggleExpandAll} variant={'contained'} color={'primary'} className={css.expandButton}>
            {allExpanded ? i18n.t('staffingPlan.collapseAll') : i18n.t('staffingPlan.expandAll')}
          </Button>
        )}

        <Stack direction='row' justifyContent={isMobile ? 'flex-start' : 'flex-end'}>
          <Typography variant='body2'>
            {i18n.t('staffingPlan.chainIncentiveSalesTarget', {
              totalTarget: incentiveSalesTargetSum,
            })}
          </Typography>
        </Stack>

        <Paper className={css.staffMemberTable}>
          {user !== 'User' && (
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent={isMobile ? 'flex-start' : 'flex-end'}
              spacing={isMobile ? 0.5 : 2}
              height={isMobile ? 'auto' : '40px'}
              flexWrap='wrap'
            >
              <div>
                <React.Fragment>
                  <span
                    className={cn({
                      [css.green]: currentIncentivesTargetSum >= totalIncentivesTarget,
                      [css.red]: currentIncentivesTargetSum < totalIncentivesTarget,
                    })}
                  >
                    {i18n.t('staffingPlan.currentIncentivesTargetSum', { sum: currentIncentivesTargetSum })}
                  </span>
                </React.Fragment>
              </div>
              <div>
                {!!totalThumbablesTarget && (
                  <React.Fragment>
                    <span
                      className={cn({
                        [css.green]: currentThumbablesTargetSum >= totalThumbablesTarget,
                        [css.red]: currentThumbablesTargetSum < totalThumbablesTarget,
                      })}
                    >{`${currentThumbablesTargetSum}`}</span>
                    <span className={css.totalTarget}>{` (${totalThumbablesTarget})`}</span>
                  </React.Fragment>
                )}
              </div>
            </Stack>
          )}
          {isMobile ? (
            this.renderEmployeeCards({
              groupedEmployees,
              isEditable,
              isToday,
              userType,
              shiftResponsibilities,
              activityDescriptions,
              defaultDescription,
              removeStaffMember,
              submit,
              toggleEditNote,
              noteIDs,
              employees,
            })
          ) : (
            <table>
              <EmployeeTableHeader
                sortInfo={sortInfo}
                toggleSortColumn={toggleSortColumn}
                toggleSortDirection={toggleSortDirection}
                isEditable={isEditable}
                userType={user.userType}
              />
              <tbody>
                {Object.keys(groupedEmployees).map((userId) => {
                  const employeeRows = groupedEmployees[userId]
                  const firstRowIndex = employees.indexOf(employeeRows[0])
                  const employeeIncentives = employeeRows.reduce(
                    (prev, cur) => {
                      return {
                        sia: prev.sia + cur.salesIncentiveActual,
                        sit: prev.sit + cur.salesIncentiveTarget,
                        tua: prev.tua + cur.thumbsUpActual,
                        tut: prev.tut + cur.thumbsUpTarget,
                      }
                    },
                    {
                      sia: 0,
                      sit: 0,
                      tua: 0,
                      tut: 0,
                    },
                  )

                  if (employeeRows.length > 1 && !expandedEmployees.includes(userId) && !isEditable) {
                    return (
                      <ExpandableEmployeeRow
                        key={userId}
                        rows={employeeRows}
                        expand={() => this.toggleExpandedEmployee(userId)}
                        firstRowIndex={firstRowIndex}
                        submit={submit}
                        valueChanged={this.onChange}
                        isToday={isToday}
                        userType={userType}
                        employeeIncentives={employeeIncentives}
                      />
                    )
                  } else {
                    const hasMultipleRows = employeeRows.length > 1
                    return getSortedEmployeeRows(employeeRows).map((employee, idx) => {
                      const uniqID = employee.userId + idx
                      const activityDescription = find(activityDescriptions, (a) => a.name === employee.position)
                      const rowIndex = employees.indexOf(employee)
                      return (
                        <EmployeeRow
                          key={uniqID}
                          idx={rowIndex}
                          employee={employee}
                          userType={userType}
                          removeStaffMember={removeStaffMember}
                          valueChanged={this.onChange}
                          isEditing={isEditable}
                          selectedEmployees={employees}
                          toggleEditNote={() => toggleEditNote(uniqID)}
                          editNote={noteIDs.findIndex((note) => note === uniqID) > -1}
                          isToday={isToday}
                          shiftResponsibilities={shiftResponsibilities}
                          activityDescription={
                            activityDescription ? activityDescription.description : defaultDescription
                          }
                          openEmployeeRowDialog={this.openEmployeeRowDialog}
                          submit={submit}
                          hasMultipleRows={hasMultipleRows}
                          showLess={() => this.toggleExpandedEmployee(employee.userId)}
                          employeeIncentives={employeeIncentives}
                        />
                      )
                    })
                  }
                })}
              </tbody>
            </table>
          )}
        </Paper>
        <Dialog
          open={isEmployeeRowDialogOpen}
          onClose={this.closeEmployeeRowDialog}
          maxWidth='md'
          fullScreen={isMobile}
          className={css.employeeDialog}
        >
          <DialogTitle className={css.employeeDialogTitle}>
            {i18n.t('admin.stations.title.activityDescriptions')}
          </DialogTitle>
          <DialogContent className={css.employeeDialogContent}>{employeeRowDialogContent}</DialogContent>
          <DialogActions className={css.employeeDialogActions}>
            <Button type='button' onClick={this.closeEmployeeRowDialog}>
              {i18n.t('buttons.cancel')}
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    )
  }
}

const getSortedEmployeeRows = <T extends { startDateTime: string }>(rows: T[]): T[] => {
  if (rows.some((row) => !row.startDateTime)) {
    return rows
  }
  return rows.slice(0).sort(sortByStartDateTimeAscending)
}

const sortByStartDateTimeAscending = (a: { startDateTime: string }, b: { startDateTime: string }): number => {
  const start = moment(a.startDateTime)
  const end = moment(b.startDateTime)
  if (start.isBefore(end)) {
    return -1
  }
  if (start.isAfter(end)) {
    return 1
  }
  return 0
}

// Functional wrapper injects the mobile breakpoint flag (useMediaQuery is a hook and
// EmployeeTableBase is a class). Below sm (600px) the table renders as stacked cards;
// at >= sm the desktop table is rendered unchanged.
const EmployeeTable = (props: any) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) // < 600px
  return <EmployeeTableBase {...props} isMobile={isMobile} />
}

export default EmployeeTable
